import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  DialogActions,
  Button,
  Typography,
  TextField,
  IconButton
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { noop } from "../../../utils/helpers";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  radioTextActive: {
    color: theme.palette.primary.main
  },
  radioTextInactive: {
    color: "rgba(0, 0, 0, 0.38)"
  },
  radioInactive: {
    color: "#DADADA"
  },
  urlBlock: {
    margin: "8px 0 16px 28px",
    color: "rgba(0, 0, 0, 0.6)"
  },
  dialogTitle: {
    padding: "28px 40px 20px"
  },
  title: {
    padding: "20px 0",
    borderBottom: "1px solid rgba(0, 0, 0, 0.2)"
  },
  dialogContent: {
    padding: "8px 40px"
  },
  closeButton: {
    position: "absolute",
    top: 28,
    right: 28
  },
  actions: {
    padding: 40
  },
  saveButton: {
    marginLeft: 24
  }
}));

const CustomizeModal = ({ open, onClose, onSave, customOption, customUrl }) => {
  const [option, setOption] = useState(customOption);
  const [twitterUrl, setTwitterUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  useEffect(() => {
    setOption(customOption);
    setTwitterUrl(customOption === "twitter" ? customUrl : "https://");
    setFacebookUrl(customOption === "facebook" ? customUrl : "https://");
    setInstagramUrl(customOption === "instagram" ? customUrl : "https://");
  }, [customOption, customUrl]);

  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const getSocialEmbedUrl = () => {
    switch (option) {
      case "twitter":
        return twitterUrl;
      case "facebook":
        return facebookUrl;
      case "instagram":
        return instagramUrl;
      default:
        return "";
    }
  };
  return (
    <Dialog
      fullScreen={fullScreen}
      onClose={onClose}
      aria-labelledby="social-media-customize-dialog-title"
      open={open}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        className={classes.dialogTitle}
        id="social-media-customize-dialog-title"
      >
        <div className={classes.title}>
          <Typography variant="h4" color="textPrimary">
            {t("socialEmbed.customizeModal.title")}
          </Typography>
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <RadioGroup
          value={option}
          onChange={ev => {
            setOption(ev.currentTarget.value);
          }}
        >
          <FormControlLabel
            value="facebook"
            control={
              <Radio
                color="primary"
                classes={{
                  root: option !== "facebook" ? classes.radioInactive : ""
                }}
              />
            }
            label={t("socialEmbed.customizeModal.facebookFeed")}
            classes={{
              label: option === "facebook" ? classes.radioTextActive : "",
              root: option !== "facebook" ? classes.radioTextInactive : ""
            }}
          />
          {option === "facebook" && (
            <div className={classes.urlBlock}>
              <Typography>
                {t("socialEmbed.customizeModal.facebookSubtitle")}
              </Typography>
              <TextField
                value={facebookUrl}
                onChange={ev => {
                  setFacebookUrl(ev.currentTarget.value);
                }}
                fullWidth
                variant="filled"
                hiddenLabel
              />
            </div>
          )}
          <FormControlLabel
            value="twitter"
            control={
              <Radio
                color="primary"
                classes={{
                  root: option !== "twitter" ? classes.radioInactive : ""
                }}
              />
            }
            label={t("socialEmbed.customizeModal.twitterFeed")}
            classes={{
              label: option === "twitter" ? classes.radioTextActive : "",
              root: option !== "twitter" ? classes.radioTextInactive : ""
            }}
          />
          {option === "twitter" && (
            <div className={classes.urlBlock}>
              <Typography>
                {t("socialEmbed.customizeModal.twitterSubtitle")}
              </Typography>
              <TextField
                value={twitterUrl}
                onChange={ev => {
                  setTwitterUrl(ev.currentTarget.value);
                }}
                fullWidth
                variant="filled"
                hiddenLabel
              />
            </div>
          )}
          <FormControlLabel
            value="instagram"
            control={
              <Radio
                color="primary"
                classes={{
                  root: option !== "instagram" ? classes.radioInactive : ""
                }}
              />
            }
            label={t("socialEmbed.customizeModal.instagramPost")}
            classes={{
              label: option === "instagram" ? classes.radioTextActive : "",
              root: option !== "instagram" ? classes.radioTextInactive : ""
            }}
          />
          {option === "instagram" && (
            <div className={classes.urlBlock}>
              <Typography>
                {t("socialEmbed.customizeModal.instagramSubtitle")}
              </Typography>
              <TextField
                value={instagramUrl}
                onChange={ev => {
                  setInstagramUrl(ev.currentTarget.value);
                }}
                fullWidth
                variant="filled"
                hiddenLabel
              />
            </div>
          )}
        </RadioGroup>
      </DialogContent>
      <DialogActions classes={{ root: classes.actions }}>
        <Button onClick={onClose} color="primary">
          {t("cancelBtn")}
        </Button>
        <Button
          onClick={() => {
            onSave({
              option,
              url: getSocialEmbedUrl()
            });
            onClose();
          }}
          className={classes.saveButton}
          color="primary"
          variant="contained"
          autoFocus
        >
          {t("saveBtn")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CustomizeModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  customOption: PropTypes.string,
  customUrl: PropTypes.string
};

CustomizeModal.defaultProps = {
  open: false,
  onClose: noop,
  onSave: noop
};

export default CustomizeModal;
